import { useEffect, useState } from "react";
import { AlertCircle, Download, Loader2, Send } from "lucide-react";
import { useTranslations } from "use-intl";
import { useInvoiceStatusLabel } from "@/i18n/useInvoiceStatusLabel";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DocumentViewerPanel from "@/components/shared/DocumentViewerPanel";
import { invoiceProvider, isDraftEditError } from "@/services/invoiceGeneration";
import type { Receivable } from "@/services/invoiceGeneration";
import { downloadInvoicePdf, invoiceDownloadName } from "./invoicePdfDownload";

type PdfState =
  | { kind: "ready"; path: string }
  | { kind: "preparing" }
  | { kind: "gone" }
  | { kind: "failed" };

interface Props {
  invoice: Receivable | null;
  onClose: () => void;
  /** Opens the list's own Mark as sent confirm for this invoice (FIN-829 F5). */
  onMarkAsSent: (invoice: Receivable) => void;
  /** A refusal of the last Mark as sent, shown in the dialog it was started from (EC9). */
  markSentError: string | null;
  busy: boolean;
}

/**
 * S2 — the View dialog (specs/draft-invoice-view-edit/screens.md): the invoice's PDF, with
 * Download, Close and, for a draft, Mark as sent. A draft that has no PDF yet gets one
 * rendered on open (EC8).
 */
export function InvoiceViewDialog({ invoice, onClose, onMarkAsSent, markSentError, busy }: Props) {
  const t = useTranslations("consultantReceivables");
  const tClose = useTranslations("common.actions");
  const statusLabel = useInvoiceStatusLabel();
  const [pdf, setPdf] = useState<PdfState>({ kind: "preparing" });
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const isDraft = invoice?.status === "draft";

  useEffect(() => {
    if (!invoice) return;
    let alive = true;
    setDownloadFailed(false);
    if (invoice.pdfPath) {
      setPdf({ kind: "ready", path: invoice.pdfPath });
      return;
    }
    if (invoice.status !== "draft") {
      setPdf({ kind: "failed" });
      return;
    }
    setPdf({ kind: "preparing" });
    invoiceProvider.ensureDraftPdf(invoice.id)
      .then((path) => { if (alive) setPdf({ kind: "ready", path }); })
      .catch((err: unknown) => {
        if (!alive) return;
        setPdf(isDraftEditError(err) && err.kind === "gone" ? { kind: "gone" } : { kind: "failed" });
      });
    return () => { alive = false; };
  }, [invoice]);

  async function download() {
    if (!invoice || pdf.kind !== "ready") return;
    setDownloading(true);
    setDownloadFailed(false);
    try {
      await downloadInvoicePdf(invoice, pdf.path);
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Dialog open={invoice !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl h-[85vh] p-0 overflow-hidden flex flex-col gap-0">
        <DialogHeader className="p-4 pb-2">
          <div className="flex items-center gap-2">
            <DialogTitle>{t("view.title", { number: invoice?.number ?? "" })}</DialogTitle>
            {isDraft && <Badge variant="outline">{statusLabel("draft")}</Badge>}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 bg-muted/30">
          {pdf.kind === "ready" && (
            <DocumentViewerPanel filePath={pdf.path} fileName={invoice ? invoiceDownloadName(invoice) : undefined} />
          )}
          {pdf.kind === "preparing" && (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground" role="status">
              <Loader2 className="h-5 w-5 animate-spin" /> {t("view.preparing")}
            </div>
          )}
          {pdf.kind === "gone" && (
            <div className="p-4">
              <Alert variant="destructive" role="alert">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t("view.goneTitle")}</AlertTitle>
                <AlertDescription>{t("view.goneBody")}</AlertDescription>
              </Alert>
            </div>
          )}
          {pdf.kind === "failed" && (
            <div className="p-4">
              <Alert variant="destructive" role="alert">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t("view.failedTitle")}</AlertTitle>
                <AlertDescription>{t("view.failedBody")}</AlertDescription>
              </Alert>
            </div>
          )}
        </div>

        {(markSentError || downloadFailed) && (
          <div className="px-4 pt-3">
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{markSentError ? t("invoices.feedback.markSentFailedTitle") : t("view.downloadFailedTitle")}</AlertTitle>
              <AlertDescription>{markSentError ?? t("view.downloadFailedBody")}</AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter className="p-4 gap-2 sm:space-x-0">
          <Button variant="outline" disabled={pdf.kind !== "ready" || downloading} onClick={download}>
            {downloading ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Download size={15} className="mr-1" />}
            {downloading ? t("view.downloading") : t("view.download")}
          </Button>
          <Button variant="outline" onClick={onClose}>{tClose("close")}</Button>
          {isDraft && invoice && pdf.kind !== "gone" && (
            <Button disabled={busy} onClick={() => onMarkAsSent(invoice)}>
              {busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Send size={15} className="mr-1" />}
              {t("invoices.markAsSent")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
